import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setProfile } from '../../../redux/slices/profile.slice';
import {
	getUserProfileService,
	patchUserProfileService,
} from '../../../shared/services/users.service';
import {
	buildFullName,
	parseFullName,
	type ParsedFullName,
} from '../../../shared/utils/parseFullName.util';
import type { RootState } from '../../../redux/store';

const buildProfilePayload = (parsed: ParsedFullName) => ({
	first_name: parsed.first_name,
	middle_name: parsed.second_name ?? '',
	father_surname: parsed.apellido_paterno,
	mother_surname: parsed.apellido_materno,
});

// Maps free-text into the profile's four name columns, tolerating partial names.
// 3+ words use the canonical parse (nombre(s) + 2 apellidos); 1–2 words are kept
// as given names with no surnames forced — so a plain "your name" step can save
// whatever the user typed without demanding a complete legal name. The mapping
// is order-preserving, so buildFullName rebuilds exactly what was typed. Returns
// null only for an empty string.
export const buildLooseNamePayload = (raw: string) => {
	const words = raw.trim().split(/\s+/).filter(Boolean);
	if (words.length === 0) return null;
	if (words.length >= 3) {
		const parsed = parseFullName(raw);
		if (parsed) return buildProfilePayload(parsed);
	}
	return {
		first_name: words[0],
		middle_name: words.slice(1).join(' '),
		father_surname: '',
		mother_surname: '',
	};
};

type UseProfileFullNameOptions = {
	// Seed shown only until the profile loads / when the profile has no name yet.
	// The profile always wins once it has a name.
	fallback?: string;
};

// Full-name field backed by the user profile — the single source of truth for
// the name. Reads the four name columns (first/middle/father/mother) and writes
// edits straight back. The name does NOT live in the onboarding record anymore.
// Shared by the consult-RFC screen and the SAT linking RFC drawer so the
// read-from-profile / write-to-profile logic lives in one place.
export const useProfileFullName = ({
	fallback = '',
}: UseProfileFullNameOptions = {}) => {
	const dispatch = useDispatch();
	const profile = useSelector((s: RootState) => s.profile.user);

	const profileFullName = buildFullName({
		first_name: profile?.first_name,
		second_name: profile?.middle_name,
		apellido_paterno: profile?.father_surname,
		apellido_materno: profile?.mother_surname,
	});

	// Draft pattern: the displayed value derives from the profile each render (so
	// it stays in sync as the profile loads), using the seed only while the
	// profile has no name. A non-null draft means the user typed and their edit
	// wins. No useEffect — the value is computed during render.
	const [draft, setDraft] = useState<string | null>(null);
	const fullName = draft ?? (profileFullName || fallback);

	// Parses the field and persists it to the profile, synchronously: PATCH, then
	// re-GET the profile and mirror it into Redux so callers can navigate knowing
	// the saved name is what the next screen will read. With an explicit
	// parsedOverride (consult-RFC, which needs a complete name for the RFC) it
	// saves exactly that; without it (a plain name step) it saves leniently —
	// partial names are kept, not rejected. Returns true on success.
	const saveToProfile = async (
		parsedOverride?: ParsedFullName,
	): Promise<boolean> => {
		const payload = parsedOverride
			? buildProfilePayload(parsedOverride)
			: buildLooseNamePayload(fullName);
		if (!payload) return false;
		try {
			await patchUserProfileService({ body: payload });
			const fresh = await getUserProfileService();
			dispatch(setProfile(fresh));
		} catch {
			return false;
		}
		setDraft(null);
		return true;
	};

	return {
		fullName,
		setFullName: (value: string) => setDraft(value),
		resetName: () => setDraft(null),
		saveToProfile,
	};
};
